import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  Search,
  RefreshCw,
  Loader2,
  Tag,
  Wand2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { DashboardLayout } from '../components/DashboardLayout';
import { BankLogo } from '../components/BankLogo';
import { DateScopeControl } from '../components/DateScopeControl';
import { StackedFlowChart } from '../components/charts/StackedFlowChart';
import { MonthlyFiguresRow } from '../components/charts/MonthlyFiguresRow';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { useBankingFilter } from '../contexts/BankingFilterContext';
import { transactionsAPI, bankAccountsAPI, matchingAPI, invoicesAPI, revolutAPI, categorizationRulesAPI, recurringCostMatchingAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';
import { CURRENCY_SYMBOLS, buildEntityLookup } from '../lib/entities';
import { formatCurrency as formatMoney, REGION_COLORS, UNASSIGNED_COLOR } from '../lib/chartFormatters';
import { groupAccountsByBank } from '../lib/bankBalancesGrouping';
import { buildCashflowSummary } from '../lib/transactionsCashflow';
import { generateMonthAxis } from '../lib/forecastChartUtils';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { TRANSACTION_CATEGORY_MAP as categoryConfig, transactionCategoryLabel } from '../lib/transactionCategory';
import {
  resolveReconciledByDestination,
  resolveReconciliationChipLabel,
  resolveReconciliationChipHref,
  resolveReconciledByLabel,
} from '../lib/reconciliationArtifact';
import { InvoicePdfNewTabLink } from '../components/InvoicePdfNewTabLink';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { XERO_ACCOUNTING_STATUS_OPTIONS, PENNYLANE_ACCOUNTING_STATUS_OPTIONS, NOT_YET_CODED_XERO_OPTION, DISPLAY_ACCOUNTING_STATUS_OPTIONS } from '../lib/accountingStatusFilter';

// Inflow and outflow render as two same-direction (both positive) bars per
// month, side by side -- never sign-split (StackedFlowChart.jsx). The
// region/reconciled-state split stays a color/grouping distinction (each
// series keeps its own hue); outflow series additionally get a reduced
// opacity so the inflow/outflow bars themselves also read as distinct at a
// glance, on top of their position.
const OUTFLOW_OPACITY = 0.55;

const CASHFLOW_REGION_SERIES = [
  { key: 'UK_in', label: 'UK', color: REGION_COLORS.UK, stackId: 'inflow' },
  { key: 'FR_in', label: 'France', color: REGION_COLORS.FR, stackId: 'inflow' },
  { key: 'Unassigned_in', label: 'Unassigned', color: UNASSIGNED_COLOR, stackId: 'inflow' },
  { key: 'UK_out', label: 'UK', color: REGION_COLORS.UK, stackId: 'outflow', opacity: OUTFLOW_OPACITY },
  { key: 'FR_out', label: 'France', color: REGION_COLORS.FR, stackId: 'outflow', opacity: OUTFLOW_OPACITY },
  { key: 'Unassigned_out', label: 'Unassigned', color: UNASSIGNED_COLOR, stackId: 'outflow', opacity: OUTFLOW_OPACITY },
];

// Reconciled green / Unreconciled amber -- same reconciliation-state
// palette used elsewhere on this page (RECONCILIATION_COLORS), now driving
// the figures row's text colour instead of a bar fill.
const CASHFLOW_FIGURES_ROWS = [
  { key: 'reconciled', countKey: 'reconciledCount', label: 'Reconciled', className: 'text-green-600 dark:text-green-500' },
  { key: 'unreconciled', countKey: 'unreconciledCount', label: 'Unreconciled', className: 'text-amber-600 dark:text-amber-500' },
];

// Full Jan..Dec spine for the current year, same generateMonthAxis('ytd')
// single source of truth the Property Portfolio "Revenue, Profit & Margin"
// chart uses -- the region chart is never windowed to months-with-data.
const CASHFLOW_MONTH_AXIS = generateMonthAxis('ytd');

// Default date scope (CLAUDE.md Commit 3.1): "From 2026" onward, not all
// time -- an operator can widen to "All time" or a custom range via
// DateScopeControl. Server-side date_from/date_to (routes/transactions.py),
// never a client-side row filter, so X-Total-Count stays correct.
const DEFAULT_DATE_SCOPE = { from: '2026-01-01' };

// Search-box debounce delay (routes/transactions.py's `search` param) --
// long enough to coalesce a normal typing burst into one request, short
// enough to still feel live.
const SEARCH_DEBOUNCE_MS = 400;

const formatCurrency = (value, currency = 'GBP') => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const absValue = Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value >= 0 ? `+${symbol}${absValue}` : `-${symbol}${absValue}`;
};


export function TransactionsPage({ embedded, onTransactionDrillDown } = {}) {
  const { selectedEntity, selectedScope, entities } = useEntityStore();
  var bankingFilter = useBankingFilter();
  var accountFilterId = bankingFilter.accountFilter;
  var accountFilterLabel = bankingFilter.accountLabel;
  var clearAccountFilter = function () { bankingFilter.setAccountFilter('', ''); };
  var txEntityFilter = bankingFilter.txEntityFilter || 'all';
  var txBankFilter = bankingFilter.txBankFilter || 'all';
  var setTxEntityFilter = bankingFilter.setTxEntityFilter;
  var setTxBankFilter = bankingFilter.setTxBankFilter;
  // Resolves by UUID (entities[].id) or canonical slug (entities[].type) --
  // every live bank-feed writer stamps entity_id as a slug, so a UUID-only
  // lookup here silently rendered blank for every bank transaction.
  const entityById = useMemo(() => buildEntityLookup(entities), [entities]);
  var PAGE_SIZE = 50;
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [bankAccounts, setBankAccounts] = useState([]);
  // Company + bank balance grouping, sourced from /bank-balances/summary
  // (same live-FX, scope-filtered endpoint the Dashboard's Bank Balances tab
  // uses) -- never the old hardcoded FX_TO_EUR table.
  const [balancesSummary, setBalancesSummary] = useState(null);
  // Monthly cash flow (region + reconciliation-state split), the single
  // source feeding both new charts and the enriched Inflow/Outflow stat
  // cards -- see lib/transactionsCashflow.js.
  const [monthlyCashflow, setMonthlyCashflow] = useState(null);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  // Banks actually present (for the current entity/scope), not a hardcoded
  // list -- a new provider shows up here automatically once it has an account.
  const bankNamesPresent = useMemo(
    () => [...new Set(bankAccounts.map((a) => a.bank_name).filter(Boolean))].sort(),
    [bankAccounts]
  );
  const [invoices, setInvoices] = useState([]);
  const [matches, setMatches] = useState([]);
  const [recurringCostMatches, setRecurringCostMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Server-side search (routes/transactions.py's `search` param, composed
  // into the same filtered query region/entity/bank already use) --
  // debounced so a normal typing burst becomes one request, not one per
  // keystroke. Fixes the search box only ever filtering the currently
  // fetched page's 50 rows client-side (a match on page 2 stayed hidden
  // unless the operator happened to already be there).
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [reconciledFilter, setReconciledFilter] = useState('all');
  // Accounting-status filter (routes/transactions.py's `accounting_status`
  // param) -- vocabulary sourced from lib/accountingStatusFilter.js, which
  // mirrors services/accounting_status.py (pinned by a backend parity
  // test), never a second hand-rolled options list.
  const [accountingStatusFilter, setAccountingStatusFilter] = useState('all');
  // France/UK region filter -- backend config.build_country_filter (the
  // SAME entity->country mapping the Invoices page's Country filter and
  // this page's own region chart already use). 'all' includes Unassigned
  // entities too; there is no selectable "Unassigned" region value on its
  // own (CLAUDE.md section 5: no match -> surface it, never a first-class
  // guessed bucket).
  const [regionFilter, setRegionFilter] = useState('all');
  const [dateScope, setDateScope] = useState(DEFAULT_DATE_SCOPE);
  const [doubleCountDialog, setDoubleCountDialog] = useState(null); // { txId, details } or null

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [selectedEntity, selectedScope, categoryFilter, reconciledFilter, txEntityFilter, txBankFilter, regionFilter, dateScope, debouncedSearchTerm, accountingStatusFilter]);

  const fetchData = useCallback(async () => {
    try {
      setFetchError(null);
      // Pass entity UUID -- resolve_entity_filter handles both UUID and type code formats.
      // The page-local entity filter (incl. "Unattributed") takes priority
      // over the sidebar's scope-derived entity when set.
      const entityTypeCode = selectedEntity !== 'all' ? selectedEntity : undefined;
      const effectiveEntityId = txEntityFilter !== 'all' ? txEntityFilter : entityTypeCode;

      const [txRes, accountsRes, summaryRes, matchesRes, invoicesRes, recurringCostMatchesRes, cashflowRes] = await Promise.all([
        transactionsAPI.getAll({
          entity_id: effectiveEntityId,
          bank_name: txBankFilter !== 'all' ? txBankFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          is_reconciled: reconciledFilter !== 'all' ? reconciledFilter === 'reconciled' : undefined,
          scope: selectedScope,
          date_from: dateScope?.from,
          date_to: dateScope?.to,
          region: regionFilter !== 'all' ? regionFilter : undefined,
          search: debouncedSearchTerm ? debouncedSearchTerm : undefined,
          // "Not yet coded (Xero)" (CLAUDE.md Prompt V item 1) shares this
          // dropdown's UI but is a DIFFERENT backend param -- it's a stored-
          // mapping-driven predicate, not part of derive_accounting_status's
          // per-row vocabulary accounting_status filters on (see
          // lib/accountingStatusFilter.js's NOT_YET_CODED_XERO_OPTION doc).
          accounting_status: (accountingStatusFilter !== 'all' && accountingStatusFilter !== NOT_YET_CODED_XERO_OPTION.key)
            ? accountingStatusFilter : undefined,
          not_yet_coded_xero: accountingStatusFilter === NOT_YET_CODED_XERO_OPTION.key ? true : undefined,
          page: page,
          page_size: PAGE_SIZE,
        }),
        // scope excludes Personal from "All Entities" -- same PERSONAL_ENTITY_IDS
        // predicate as GET /bank-balances/summary, so the two can't drift.
        bankAccountsAPI.getAll(entityTypeCode, selectedScope),
        bankAccountsAPI.getSummary({ scope: selectedScope, entity_id: entityTypeCode }),
        matchingAPI.getAll(),
        invoicesAPI.getAll({ limit: 500 }),
        recurringCostMatchingAPI.getConfirmed(),
        // Same entity/bank/category/date_range filters as the row list above
        // -- deliberately WITHOUT is_reconciled, so the reconciliation-split
        // chart and card sub-lines stay meaningful even while the row table
        // is filtered to one Reconciled/Unreconciled bucket below.
        transactionsAPI.getMonthlyCashflow({
          entity_id: effectiveEntityId,
          bank_name: txBankFilter !== 'all' ? txBankFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          scope: selectedScope,
          date_from: dateScope?.from,
          date_to: dateScope?.to,
          region: regionFilter !== 'all' ? regionFilter : undefined,
          search: debouncedSearchTerm ? debouncedSearchTerm : undefined,
        }),
      ]);

      const txData = txRes.data;
      setTransactions(txData.transactions || []);
      setTxTotal(txData.total || 0);
      setBankAccounts(accountsRes.data);
      setBalancesSummary(summaryRes.data);
      setMatches(matchesRes.data);
      setInvoices(invoicesRes.data);
      setRecurringCostMatches(recurringCostMatchesRes.data || []);
      setMonthlyCashflow(cashflowRes.data);
    } catch (error) {
      setFetchError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [selectedEntity, selectedScope, categoryFilter, reconciledFilter, txEntityFilter, txBankFilter, regionFilter, dateScope, debouncedSearchTerm, accountingStatusFilter, page]);

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchData();
    }
  }, [fetchData, entities]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      // Sync Revolut accounts first (real data from connected accounts)
      try { await revolutAPI.sync(); } catch { /* no connected accounts — ok */ }
      // Then mock sync fills in entities without real connections
      await bankAccountsAPI.sync();
      await fetchData();
      toast.success('Transactions synced');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCategorize = async (txId, category, { force = false } = {}) => {
    try {
      await transactionsAPI.categorize(txId, category, { force });
      toast.success(
        force
          ? `Categorized as ${transactionCategoryLabel(category)} (may double-count with linked invoice/booking)`
          : 'Transaction categorized'
      );
      setDoubleCountDialog(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.error === 'double_count_risk') {
        setDoubleCountDialog({ txId, category, details: error.response.data.details });
      } else {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleAutoMatch = async () => {
    setIsMatching(true);
    try {
      // No entity restriction — a transaction from Entity A may settle an invoice addressed to Entity B
      const res = await matchingAPI.autoMatch(undefined);
      let msg = `Auto-matched ${res.data.matches_created} transaction${res.data.matches_created !== 1 ? 's' : ''}`;
      if (res.data.duplicate_candidate_transactions > 0) {
        msg += ` (${res.data.duplicate_candidate_transactions} need a manual pick between multiple candidates)`;
      }
      toast.success(msg);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsMatching(false);
    }
  };

  const handleConfirmMatch = async (matchId) => {
    try {
      await matchingAPI.confirm(matchId);
      toast.success('Match confirmed');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRejectMatch = async (matchId) => {
    try {
      await matchingAPI.reject(matchId);
      toast.success('Match rejected');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateRuleFromTx = async (tx) => {
    try {
      const res = await categorizationRulesAPI.fromTransaction(tx.id);
      const prefilled = res.data;
      // Navigate to rules page with prefilled data via sessionStorage
      sessionStorage.setItem('prefilled_rule', JSON.stringify(prefilled));
      window.location.href = '/categorization-rules';
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Search is now server-side (routes/transactions.py's `search` param,
  // via debouncedSearchTerm above) -- `transactions` already IS the
  // searched page. Only the account drill-through filter (URL ?account=
  // param) still narrows client-side, same as before.
  const filteredTransactions = accountFilterId
    ? transactions.filter((tx) => tx.bank_account_id === accountFilterId)
    : transactions;

  const bankAccountById = Object.fromEntries(bankAccounts.map((a) => [a.id, a]));
  const invoiceById = Object.fromEntries(invoices.map((inv) => [inv.id, inv]));
  const suggestedMatchByTxId = Object.fromEntries(
    matches.filter((m) => m.status === 'suggested').map((m) => [m.transaction_id, m])
  );
  const recurringCostMatchByTxId = Object.fromEntries(
    recurringCostMatches.map((m) => [m.transaction_id, m])
  );

  // Live backend FX rates (same fx_rates_spot the Dashboard's Bank Balances
  // tab uses), never the old hardcoded FX_TO_EUR table.
  const fxRatesSpot = balancesSummary?.fx_rates_spot || {};
  const summaryAccounts = balancesSummary?.accounts || [];

  // Grouped by bank overall, for the Total Balance stat card.
  const bankGroups = useMemo(
    () => groupAccountsByBank(summaryAccounts, displayCurrency, fxRatesSpot),
    [summaryAccounts, displayCurrency, fxRatesSpot]
  );

  const unreconciledTx = transactions.filter((t) => !t.is_reconciled);
  const unreconciled = unreconciledTx.length;

  // Total Balance: same per-account parse+convert path as the bank-group
  // subtotals (computeGroupSubtotal, via groupAccountsByBank), so the
  // headline total always reconciles with the grouped rows below it.
  const totalBalanceDisplay = bankGroups.reduce((sum, g) => sum + g.subtotal, 0);

  // Monthly cash flow (full filtered scope, not just this page's 50 rows),
  // split by region and by reconciliation state -- single derivation
  // feeding both new charts and the Inflow/Outflow stat cards below, so
  // they cannot disagree (see lib/transactionsCashflow.js).
  const cashflowSummary = useMemo(
    () => buildCashflowSummary(monthlyCashflow?.rows || [], CASHFLOW_MONTH_AXIS, {
      displayCurrency, fxRatesSpot,
    }),
    [monthlyCashflow, displayCurrency, fxRatesSpot]
  );
  const totalInflowDisplay = cashflowSummary.totals.inflow.total;
  const totalOutflowDisplay = cashflowSummary.totals.outflow.total;

  var content = (
      <div className="space-y-6" data-testid="transactions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Bank Transactions</h1>
            <p className="text-muted-foreground mt-1">View and categorize bank transactions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleAutoMatch} disabled={isMatching}>
              <Tag className={cn('h-4 w-4 mr-2', isMatching && 'animate-pulse')} />
              Auto-match
            </Button>
            <Button onClick={handleSync} disabled={isSyncing} data-testid="sync-transactions-btn">
              <RefreshCw className={cn('h-4 w-4 mr-2', isSyncing && 'animate-spin')} />
              Sync
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="sm:col-span-1">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Balance</p>
              <p className="text-2xl font-bold font-mono mt-0.5">
                {formatMoney(totalBalanceDisplay, displayCurrency)}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">{summaryAccounts.length} accounts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Inflow</p>
              <p className="text-xl font-bold font-mono text-green-600 dark:text-green-500 mt-0.5">
                {formatMoney(totalInflowDisplay, displayCurrency)}
              </p>
              <p className="text-[11px] text-muted-foreground/70 mt-1 leading-tight">
                UK {formatMoney(cashflowSummary.totals.inflow.UK, displayCurrency)} · France {formatMoney(cashflowSummary.totals.inflow.FR, displayCurrency)}
                {cashflowSummary.totals.inflow.Unassigned !== 0 && <> · Unassigned {formatMoney(cashflowSummary.totals.inflow.Unassigned, displayCurrency)}</>}
              </p>
              <p className="text-[11px] text-muted-foreground/70 leading-tight">
                Reconciled {formatMoney(cashflowSummary.totals.inflow.Reconciled, displayCurrency)} · Unreconciled {formatMoney(cashflowSummary.totals.inflow.Unreconciled, displayCurrency)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Outflow</p>
              <p className="text-xl font-bold font-mono text-red-600 dark:text-red-500 mt-0.5">
                {formatMoney(totalOutflowDisplay, displayCurrency)}
              </p>
              <p className="text-[11px] text-muted-foreground/70 mt-1 leading-tight">
                UK {formatMoney(cashflowSummary.totals.outflow.UK, displayCurrency)} · France {formatMoney(cashflowSummary.totals.outflow.FR, displayCurrency)}
                {cashflowSummary.totals.outflow.Unassigned !== 0 && <> · Unassigned {formatMoney(cashflowSummary.totals.outflow.Unassigned, displayCurrency)}</>}
              </p>
              <p className="text-[11px] text-muted-foreground/70 leading-tight">
                Reconciled {formatMoney(cashflowSummary.totals.outflow.Reconciled, displayCurrency)} · Unreconciled {formatMoney(cashflowSummary.totals.outflow.Unreconciled, displayCurrency)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Unreconciled</p>
              <p className="text-xl font-bold font-mono text-orange-600 dark:text-orange-500 mt-0.5">
                {unreconciled}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">pending review</p>
            </CardContent>
          </Card>
        </div>
        {/* A single unconditional wrapper (not two independent -mt-4
            siblings) so the always-on "Converted to..." line and the
            conditionally-rendered unconverted note never depend on
            cross-sibling negative-margin collapsing against space-y-6. */}
        <div className="-mt-4 flex flex-col items-end gap-0.5">
          <p
            className={
              balancesSummary?.fx_rate_stale
                ? 'text-xs text-amber-600 dark:text-amber-500 text-right'
                : 'text-xs text-muted-foreground/50 text-right'
            }
            data-testid="fx-rate-freshness-note"
          >
            Converted to {displayCurrency} ·{' '}
            {balancesSummary?.fx_rate_as_of
              ? `rates as of ${new Date(balancesSummary.fx_rate_as_of).toLocaleString('en-GB')}`
              : 'rates unavailable'}
          </p>
          {cashflowSummary.unconverted.length > 0 && (
            <p
              className="text-xs text-amber-600 dark:text-amber-500 text-right"
              data-testid="cashflow-unconverted-note"
              title="No FX rate is configured for these currencies -- excluded from every total and chart above, shown here in their own native currency instead of being silently treated as 1:1."
            >
              Unconverted (no FX rate):{' '}
              {cashflowSummary.unconverted
                .map((u) => formatMoney(u.inflow + u.outflow, u.currency))
                .join(' · ')}
            </p>
          )}
        </div>

        {/* Monthly cash flow, full-width region split, Jan..Dec of the
            current year -- per-month Reconciled/Unreconciled figures render
            below the chart instead of as a second chart. Per-account
            balances remain reachable on the Balances tab. */}
        <StackedFlowChart
          title="Monthly Cash Flow by Region"
          data={cashflowSummary.byRegion}
          series={CASHFLOW_REGION_SERIES}
          displayCurrency={displayCurrency}
          data-testid="cashflow-chart-region"
          footer={
            <MonthlyFiguresRow
              data={cashflowSummary.figuresRow}
              rows={CASHFLOW_FIGURES_ROWS}
              displayCurrency={displayCurrency}
            />
          }
        />

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="transaction-search-input"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categoryConfig).map(([key, value]) => (
                    <SelectItem key={key} value={key}>{value.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={reconciledFilter} onValueChange={setReconciledFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="reconciled">Reconciled</SelectItem>
                  <SelectItem value="unreconciled">Unreconciled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={txEntityFilter} onValueChange={setTxEntityFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="entity-filter-select">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="unassigned">Unattributed</SelectItem>
                  {entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={txBankFilter} onValueChange={setTxBankFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="bank-filter-select">
                  <SelectValue placeholder="Bank" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Banks</SelectItem>
                  {bankNamesPresent.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* France/UK region -- constrains the row list AND every
                  aggregate below (inflow/outflow totals, the cash-flow
                  chart, the figures row) via the same backend filter, same
                  rule as the filters above. No standalone "Unassigned"
                  option -- Unassigned entities are visible only under All. */}
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="region-filter-select">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="UK">UK</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                </SelectContent>
              </Select>
              {/* Accounting-system push/reconciliation status -- server-side
                  (routes/transactions.py's accounting_status param), composed
                  with every other filter above including search. Options come
                  from lib/accountingStatusFilter.js, which mirrors backend
                  services/accounting_status.py's own vocabulary (parity-
                  tested), never a second hand-rolled list. Doubles as the
                  census the operator needs: the Showing X of Y line below
                  becomes an exact count for the selected status once this
                  narrows the query. */}
              <Select value={accountingStatusFilter} onValueChange={setAccountingStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="accounting-status-filter-select">
                  <SelectValue placeholder="Accounting" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Accounting Status</SelectItem>
                  {/* Primary, operator-facing choice: the same two words the
                      column itself now renders. The precise per-system
                      statuses below stay as diagnostics. */}
                  <SelectGroup>
                    <SelectLabel>Status</SelectLabel>
                    {DISPLAY_ACCOUNTING_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.key} value={opt.key}>
                        <div className="flex flex-col">
                          <span>{opt.label}</span>
                          <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Xero (detail)</SelectLabel>
                    {XERO_ACCOUNTING_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.key} value={opt.key}>
                        {opt.description ? (
                          <div className="flex flex-col">
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                          </div>
                        ) : opt.label}
                      </SelectItem>
                    ))}
                    {/* Not part of XERO_ACCOUNTING_STATUS_OPTIONS -- a stored-
                        mapping-driven predicate, not a per-row derived Xero
                        status (see lib/accountingStatusFilter.js). */}
                    <SelectItem key={NOT_YET_CODED_XERO_OPTION.key} value={NOT_YET_CODED_XERO_OPTION.key}>
                      <div className="flex flex-col">
                        <span>{NOT_YET_CODED_XERO_OPTION.label}</span>
                        <span className="text-[10px] text-muted-foreground">{NOT_YET_CODED_XERO_OPTION.description}</span>
                      </div>
                    </SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Pennylane</SelectLabel>
                    {PENNYLANE_ACCOUNTING_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <DateScopeControl value={dateScope} onChange={setDateScope} defaultFrom={DEFAULT_DATE_SCOPE.from} />
            </div>
            {accountFilterId && (
              <div className="flex items-center gap-2 mt-3" data-testid="account-filter-chip">
                <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1 text-xs">
                  Account: {accountFilterLabel || accountFilterId}
                  <button
                    onClick={clearAccountFilter}
                    className="ml-1 hover:text-destructive"
                    data-testid="clear-account-filter"
                    aria-label="Clear account filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : fetchError ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                <h3 className="text-lg font-medium mb-2">Failed to load data</h3>
                <p className="text-muted-foreground mb-4">{fetchError}</p>
                <Button onClick={() => { setIsLoading(true); fetchData(); }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Bank</th>
                      <th>Entity</th>
                      <th>Description</th>
                      <th>Payer / Receiver</th>
                      <th>Category</th>
                      <th>Method</th>
                      <th>Amount</th>
                      {/* Fan, 29 Aug: two systems, two columns, named. This
                          one is Finance Agent's OWN reconciliation state;
                          Accounting reports what the accounting software
                          (Xero/Pennylane) says. Conflating them hid whether
                          the ledger had ever been squared. */}
                      <th>Reconciliation (Finance Agent)</th>
                      <th>Reconciled By</th>
                      <th>Accounting (Xero / Pennylane)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((tx, index) => (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.01 }}
                        data-testid={`transaction-row-${tx.id}`}
                      >
                        <td className="text-muted-foreground whitespace-nowrap">
                          {format(new Date(tx.date), 'dd MMM yyyy')}
                        </td>
                        <td>
                          {(() => {
                            const acc = bankAccountById[tx.bank_account_id];
                            if (!acc) return <span className="text-muted-foreground/40 text-xs">—</span>;
                            return (
                              <div className="flex items-center gap-1.5">
                                <BankLogo bankName={acc.bank_name} size="sm" />
                                <span className="text-xs text-muted-foreground truncate max-w-[72px]">
                                  {acc.bank_name}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="font-medium">
                          {entityById(tx.entity_id)?.name ?? <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="max-w-[200px] truncate">
                          {tx.description || <span className="text-muted-foreground/40">—</span>}
                          {tx.reference && (
                            <span
                              className="block text-[10px] text-muted-foreground/60 truncate"
                              title={`Ref: ${tx.reference}`}
                              data-testid={`transaction-reference-${tx.id}`}
                            >
                              Ref: {tx.reference}
                            </span>
                          )}
                        </td>
                        <td className="text-sm">{tx.counterparty || <span className="text-muted-foreground/40">—</span>}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Select
                              value={tx.category}
                              onValueChange={(value) => handleCategorize(tx.id, value)}
                            >
                              <SelectTrigger className="h-8 w-[130px]">
                                <SelectValue>
                                  <Badge className={categoryConfig[tx.category]?.color}>
                                    {categoryConfig[tx.category]?.label}
                                  </Badge>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(categoryConfig).map(([key, value]) => (
                                  <SelectItem key={key} value={key}>
                                    <Badge className={value.color}>{value.label}</Badge>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              title="Create rule from this transaction"
                              onClick={() => handleCreateRuleFromTx(tx)}
                            >
                              <Wand2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          {tx.categorized_by && tx.categorized_by !== 'manual' && (
                            <span className="text-[10px] text-muted-foreground/60 block mt-0.5">
                              {tx.categorized_by === 'rule' ? 'auto' : tx.categorized_by}
                              {tx.rule_id && ` (${tx.rule_id.slice(0, 8)})`}
                            </span>
                          )}
                        </td>
                        <td>
                          {tx.payment_method ? (
                            <span className={cn(
                              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                              tx.payment_method === 'bank_transfer' && 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/20 dark:text-blue-400',
                              tx.payment_method === 'card_payment' && 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-900/20 dark:text-violet-400',
                              tx.payment_method === 'withdrawal' && 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-900/20 dark:text-orange-400',
                            )}>
                              {tx.payment_method === 'bank_transfer' && 'Bank Transfer'}
                              {tx.payment_method === 'card_payment' && 'Card Payment'}
                              {tx.payment_method === 'withdrawal' && 'Withdrawal'}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </td>
                        <td className={cn(
                          'font-mono font-medium whitespace-nowrap',
                          tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {formatCurrency(tx.amount, tx.currency)}
                        </td>
                        <td>
                          {(() => {
                            const txEntityId = tx.entity_id;

                            if (tx.is_reconciled) {
                              const recurringMatch = tx.matched_recurring_cost_match_id
                                ? recurringCostMatchByTxId[tx.id]
                                : null;
                              if (recurringMatch) {
                                const linkedInv = invoiceById[recurringMatch.linked_invoice_id];
                                return (
                                  <div className="flex flex-col gap-1">
                                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 w-fit">
                                      Reconciled
                                    </Badge>
                                    <Badge variant="outline" className="text-xs w-fit" title={`Matched by recurring cost (${recurringMatch.month || ''})`}>
                                      {recurringMatch.cost_type_label || 'Recurring cost'}
                                      {recurringMatch.month ? ` · ${recurringMatch.month}` : ''}
                                    </Badge>
                                    {recurringMatch.memory_boosted && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1 py-0 w-fit border-violet-400 text-violet-700 dark:border-violet-600 dark:text-violet-400"
                                        title="This match was rescued by a learned counterparty pattern (reconciliation memory)"
                                      >
                                        matched by memory ({recurringMatch.memory_confirm_count || 1}x prior)
                                      </Badge>
                                    )}
                                    {linkedInv && (
                                      <Badge variant="outline" className="font-mono text-xs w-fit">
                                        {linkedInv.invoice_number}
                                      </Badge>
                                    )}
                                  </div>
                                );
                              }

                              const inv = invoiceById[tx.matched_invoice_id];
                              const crossEntity = inv && inv.entity_id !== txEntityId;
                              const bankFee = tx.reconciled_by?.kind === 'invoice'
                                ? tx.reconciled_by.bank_fee_amount : null;
                              // BR Revenue / Supplier Payment chip: driven by the
                              // server-computed reconciled_by.display_ref (never
                              // the capped invoiceById join), same as the Rent
                              // chip above reads from its own dedicated,
                              // uncapped source -- see resolveReconciliationChipLabel
                              // (lib/reconciliationArtifact.js) for why this needs
                              // its own truncation, distinct from the Reconciled
                              // By column's fuller display_ref text.
                              const chipLabel = resolveReconciliationChipLabel(tx.reconciled_by);
                              const chipHref = resolveReconciliationChipHref(tx.reconciled_by);
                              const chipBadge = chipLabel && (
                                <Badge variant="outline" className="text-xs w-fit" title={chipLabel}>
                                  {chipLabel}
                                </Badge>
                              );
                              return (
                                <div className="flex flex-col gap-1">
                                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 w-fit">
                                    Reconciled
                                  </Badge>
                                  {chipBadge && (chipHref ? (
                                    <Link to={chipHref} className="w-fit" data-testid={`reconciliation-chip-link-${tx.id}`}>
                                      {chipBadge}
                                    </Link>
                                  ) : chipBadge)}
                                  {bankFee > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1 py-0 w-fit border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-400"
                                      title="Shortfall auto-closed as a bank/transfer fee (services/bank_fee_tolerance.py)"
                                    >
                                      {/* tx.reconciled_by.bank_fee_currency, never inv.currency -- inv comes
                                          from a frontend-only join against invoicesAPI.getAll({ limit: 500 }),
                                          a bounded window the settling invoice for a fee-bearing match is not
                                          guaranteed to fall inside (2026-08-13 incident: this badge crashed
                                          the whole page reading inv.currency off an undefined inv). The fee's
                                          amount AND currency are both server-computed at confirm time
                                          (routes/transactions.py::_batch_reconciled_by) and served directly on
                                          the row's own reconciled_by -- this badge never needs the invoice
                                          join at all. */}
                                      fee {formatCurrency(bankFee, tx.reconciled_by?.bank_fee_currency)}
                                    </Badge>
                                  )}
                                  {/* Invoice number lives in the Reconciled By column only
                                      (resolveReconciledByLabel) -- never duplicated here. */}
                                  {inv && crossEntity && (
                                    <span className="text-[10px] text-amber-600 dark:text-amber-400">
                                      → {entityById(inv.entity_id)?.name ?? 'other entity'}
                                    </span>
                                  )}
                                </div>
                              );
                            }

                            const suggested = suggestedMatchByTxId[tx.id];
                            if (suggested) {
                              const inv = invoiceById[suggested.invoice_id];
                              const crossEntity = inv && inv.entity_id !== txEntityId;
                              return (
                                <div className="flex flex-col gap-1.5">
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-1">
                                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 w-fit text-xs">
                                        {suggested.confidence}
                                      </Badge>
                                      {inv && (
                                        <span className="text-xs font-mono text-muted-foreground">
                                          {inv.invoice_number}
                                        </span>
                                      )}
                                    </div>
                                    {crossEntity && (
                                      <span className="text-[10px] text-amber-600 dark:text-amber-400">
                                        → {entityById(inv.entity_id)?.name ?? 'other entity'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => handleConfirmMatch(suggested.id)}
                                    >
                                      Confirm
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => handleRejectMatch(suggested.id)}
                                    >
                                      Reject
                                    </Button>
                                  </div>
                                </div>
                              );
                            }

                            return onTransactionDrillDown ? (
                              <Badge
                                variant="outline"
                                className="cursor-pointer hover:bg-muted"
                                onClick={(e) => { e.stopPropagation(); onTransactionDrillDown(tx.id); }}
                                data-testid={'drill-recon-' + tx.id}
                                title="Finance Agent has not reconciled this transaction and has no suggested match for it"
                              >
                                Unreconciled
                              </Badge>
                            ) : (
                              <Badge variant="outline" title="Finance Agent has not reconciled this transaction and has no suggested match for it">Unreconciled</Badge>
                            );
                          })()}
                        </td>
                        {/* Reconciled By: deep-links to the artifact BEHIND the
                            reconciliation (single mapping in
                            lib/reconciliationArtifact.js) -- the settling BR OS/
                            supplier invoice's PDF, the rent cost's Property
                            Portfolio Costs row, or the management statement, never
                            just the Reconciliation page. rb is server-computed by
                            routes/transactions.py (_batch_reconciled_by) -- only
                            ever the active match, never a superseded/orphaned one. */}
                        <td>
                          {(() => {
                            const rb = tx.reconciled_by;
                            if (!rb) return <span className="text-muted-foreground/40 text-xs">—</span>;
                            if (rb.match_missing) {
                              return (
                                <span className="text-muted-foreground/40 text-xs" title="Match missing">—</span>
                              );
                            }
                            const dest = resolveReconciledByDestination(rb);
                            const reconciledByLabel = resolveReconciledByLabel(rb);
                            if (dest.type === 'pdf') {
                              return (
                                <div className="flex items-center gap-1.5" data-testid={`reconciled-by-${tx.id}`}>
                                  <span className="text-xs font-mono">{reconciledByLabel}</span>
                                  <InvoicePdfNewTabLink invoiceId={dest.invoiceId} pdfSource={dest.pdfSource} />
                                </div>
                              );
                            }
                            return (
                              <Link
                                to={dest.href}
                                className="text-xs font-mono text-blue-600 hover:underline dark:text-blue-400"
                                title={rb.kind.replace(/_/g, ' ')}
                                data-testid={`reconciled-by-${tx.id}`}
                              >
                                {reconciledByLabel}
                              </Link>
                            );
                          })()}
                        </td>
                        {/* Accounting status, ONE server-derived source
                            (services/accounting_status.py, wired through routes/
                            transactions.py) covering every reconciliation class --
                            Pennylane invoice, Xero invoice, BR OS invoice, management
                            statement, recurring cost match. Rendered as the operator
                            vocabulary Reconciled / Unreconciled (never a dash, never
                            internal plumbing wording); the precise derived status and
                            its reason live in the tooltip. */}
                        <td>
                          {(() => {
                            const acc = tx.accounting_status;
                            // Fan, 28 Aug: this column reads "Reconciled" or
                            // "Unreconciled" and nothing else. The precise
                            // derived status (No Xero record, Queued, Not
                            // applicable, ...) is diagnostic detail and moves
                            // into the tooltip. The collapse itself is
                            // server-side (services/accounting_status.py::
                            // to_display_state) so this stays a renderer, not
                            // a second derivation -- with one exception: a row
                            // with NO derived status at all is unreconciled by
                            // definition, and rendering a dash there was the
                            // "operator has to guess" bug this column already
                            // fixed once.
                            const displayStatus = acc?.display_status || 'unreconciled';
                            const isReconciled = displayStatus === 'reconciled';
                            const className = isReconciled
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700';
                            const asOfText = acc?.as_of ? `as of ${acc.as_of}` : null;
                            const title = [
                              acc?.detail_label || acc?.label,
                              acc?.reason,
                              acc?.error,
                              asOfText,
                            ].filter(Boolean).join(' -- ') || undefined;
                            return (
                              <Badge
                                variant="secondary"
                                className={cn('text-xs', className)}
                                title={title}
                                data-testid={`accounting-status-${tx.id}`}
                                data-accounting-display-status={displayStatus}
                                data-accounting-detail-status={acc?.status || 'none'}
                              >
                                {acc?.display_label || (isReconciled ? 'Reconciled' : 'Unreconciled')}
                              </Badge>
                            );
                          })()}
                          {/* Ledger-reconciled readout (Direction 2): shows what the
                              accounting system itself says, independent of whether FA
                              has reconciled this row yet. For a Pennylane-ledger entity
                              the badge above only speaks once tx.is_reconciled is true,
                              so this is the one place a not-yet-FA-matched row can still
                              show "the ledger already has this" (a Xero-ledger entity's
                              badge above already covers this on its own, 12 Aug fix).
                              counterpart_display_ref is resolved server-side (routes/
                              transactions.py); Xero never auto-confirms on the strength
                              of this, it's display only. */}
                          {(() => {
                            const ar = tx.accounting_reconciliation;
                            if (!ar || !ar.reconciled) return null;
                            const label = ar.counterpart_source_label || (ar.source_system === 'xero' ? 'Xero' : 'Pennylane');
                            const text = ar.counterpart_display_ref
                              ? `Reconciled in ${label} as: ${ar.counterpart_display_ref}`
                              : `Reconciled in ${label}`;
                            return (
                              <div
                                className="text-[10px] text-muted-foreground mt-0.5"
                                data-testid={`accounting-reconciliation-readout-${tx.id}`}
                              >
                                {text}
                              </div>
                            );
                          })()}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Pagination */}
        {txTotal > 0 && (() => {
          var totalPages = Math.max(1, Math.ceil(txTotal / PAGE_SIZE));
          return (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, txTotal)}{'\u2013'}{Math.min(page * PAGE_SIZE, txTotal)} of {txTotal.toLocaleString()} transactions
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          );
        })()}
        {/* Double-count warning dialog */}
        <AlertDialog open={!!doubleCountDialog} onOpenChange={(open) => { if (!open) setDoubleCountDialog(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>This transaction is already counted in revenue</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {doubleCountDialog?.details?.matched_invoice_id && (
                    <p>
                      Reconciled to invoice <span className="font-mono font-medium text-foreground">{doubleCountDialog.details.matched_invoice_id}</span>
                      {doubleCountDialog.details.matched_invoice_amount != null && (
                        <> ({formatMoney(Number(doubleCountDialog.details.matched_invoice_amount), doubleCountDialog.details.matched_invoice_currency || 'GBP')})</>
                      )}
                    </p>
                  )}
                  {doubleCountDialog?.details?.matched_reservation_id && (
                    <p>
                      Matched to {doubleCountDialog.details.matched_reservation_source} reservation <span className="font-mono font-medium text-foreground">{doubleCountDialog.details.matched_reservation_id}</span>
                    </p>
                  )}
                  <p>Categorizing as "{transactionCategoryLabel(doubleCountDialog?.category)}" will double-count this amount in revenue reports.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => handleCategorize(doubleCountDialog.txId, doubleCountDialog.category, { force: true })}
              >
                Categorize anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}

export function TransactionsPageContent(props) {
  return <TransactionsPage embedded {...props} />;
}
